import { colorToScore, scoreTextOn } from './areaData.js';
import { getAreaLabel } from './listingStory.js';
import './areaCard.css';
import { homeTypeIconSvg } from './homeTypeIcons.js';
import { areaPriceTrendPlaceholder, installAreaPriceTrendObserver } from './areaPriceTrend.js';
import { areaShareButton, installAreaShareHandler } from './areaShare.js';

// The area card in kartvy: the hover card at the bottom and the sticky popup (double click). Written like
// a neighbour would describe the area: a badge in the score colour, the area's character, one sentence
// about its strengths, four everyday facts and the two sqm prices. Same tone as listingStory.js: strengths
// are named, nothing is described as worse.

const NBSP = ' ';
const missing = (value) => value == null || value === '' || !Number.isFinite(Number(value)) || Number(value) < 0;
const formatNumber = (value, digits, swedish) => Number(value)
    .toLocaleString(swedish ? 'sv-SE' : 'en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits })
    .replace(/\s/g, NBSP);

// Area price per m²; 0 or -1 means too few sales to show one.
const formatSqmPrice = (value, swedish) => (Number(value) > 0
    ? `${Math.round(value / 1000)}${NBSP}${swedish ? 'tkr/m²' : 'kSEK/m²'}`
    : (swedish ? 'Få sålda' : 'Few sales'));

// The site's home-type icons (homeTypeIcons.js).
const HOUSE_ICON = homeTypeIconSvg('house', { strokeWidth: 2 });
const APARTMENT_ICON = homeTypeIconSvg('apartment', { strokeWidth: 1.9 });
const VILLA_ICON = homeTypeIconSvg('house', { strokeWidth: 1.9 });

// One sentence from the area's strengths (score 4+ against nearby areas), strongest first. Also the area
// page's summary (src/area/AreaPage.js).
export const buildSummary = ({ scores, ownedShare }, swedish) => {
    const phrases = [];
    const add = (strength, sv, en) => phrases.push({ strength, text: swedish ? sv : en });
    if (Number.isFinite(ownedShare) && ((scores.owned ?? 0) >= 4 || ownedShare >= 0.6)) {
        add(scores.owned ?? 4,
            ownedShare >= 0.5 ? 'de flesta äger sitt boende' : 'många äger sitt boende',
            ownedShare >= 0.5 ? 'most people own their home' : 'many own their home');
    }
    if ((scores.education ?? 0) >= 4) add(scores.education, 'många har läst vidare efter gymnasiet', 'many have studied after upper secondary school');
    if ((scores.income ?? 0) >= 4) add(scores.income, 'inkomsterna är höga', 'incomes are high');
    if ((scores.unemployment ?? 0) >= 4) add(scores.unemployment, 'få står utan jobb', 'few are out of work');
    const priceScores = [scores.apartmentPrice, scores.housePrice].filter((score) => score != null);
    if (priceScores.length && Math.min(...priceScores) <= 2.5) {
        add(7 - Math.min(...priceScores), 'priserna är lägre än i närområdet', 'prices are lower than in nearby areas');
    }
    const picked = phrases.sort((a, b) => b.strength - a.strength).slice(0, 2).map((phrase) => phrase.text);
    if (!picked.length) {
        if (Number.isFinite(ownedShare) && ownedShare < 0.25) {
            return swedish ? 'Mest hyresrätter här.' : 'Mostly rentals here.';
        }
        if (Number.isFinite(ownedShare) && ownedShare < 0.5) {
            return swedish ? 'Här finns både ägda bostäder och hyresrätter.' : 'A mix of owned homes and rentals.';
        }
        return swedish ? 'Mycket här är som i närområdet.' : 'Much like the nearby areas.';
    }
    const sentence = picked.join(swedish ? ' och ' : ' and ');
    return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
};

// The card's numbers for one area: the metric scores, the share who own their home, the four everyday facts
// (income a month, owners in 10, highly educated, unemployment) and the two sqm prices. Also the area page's.
export const getAreaFacts = (regSo, colorCache, areaStats, swedish) => {
    const stat = (prefix) => areaStats[`${prefix}${regSo}`];
    const metricColor = (prefix) => colorCache[`${prefix}${regSo}`] || 'var(--area-card-dot-empty)';
    const metricScore = (prefix) => colorToScore[colorCache[`${prefix}${regSo}`]] ?? null;

    const ownedShare = missing(stat('apartment_')) ? NaN : Number(stat('apartment_'));
    const scores = {
        owned: metricScore('apartment_'),
        education: metricScore('education_'),
        income: metricScore('netInc_'),
        unemployment: metricScore('unemployment_'),
        apartmentPrice: Number(stat('sqm_price_apartment_')) > 0 ? metricScore('sqm_price_apartment_') : null,
        housePrice: Number(stat('sqm_price_house_')) > 0 ? metricScore('sqm_price_house_') : null,
    };

    const income = stat('netInc_');
    const education = stat('education_');
    const unemployment = stat('unemployment_');
    const facts = [
        {
            key: 'income',
            score: scores.income,
            value: missing(income) ? '–' : `${formatNumber(Math.round(income / 12 / 100) * 100, 0, swedish)}${NBSP}kr`,
            caption: swedish ? 'i månaden efter skatt' : 'a month after tax',
            color: metricColor('netInc_'),
        },
        {
            key: 'owned',
            score: scores.owned,
            value: Number.isFinite(ownedShare) ? `${Math.round(ownedShare * 10)} ${swedish ? 'av' : 'in'} 10` : '–',
            caption: swedish ? 'äger sitt boende' : 'own their home',
            color: metricColor('apartment_'),
        },
        {
            key: 'education',
            score: scores.education,
            value: missing(education) ? '–' : `${formatNumber(education, 0, swedish)}${NBSP}%`,
            caption: swedish ? 'högutbildade' : 'highly educated',
            color: metricColor('education_'),
        },
        {
            key: 'unemployment',
            score: scores.unemployment,
            value: missing(unemployment) ? '–' : `${formatNumber(unemployment, 1, swedish)}${NBSP}%`,
            caption: swedish ? 'arbetslöshet' : 'unemployment',
            color: metricColor('unemployment_'),
        },
    ];
    const prices = [
        { key: 'apartmentPrice', score: scores.apartmentPrice, icon: APARTMENT_ICON, label: swedish ? 'Lägenhet' : 'Apartment', value: formatSqmPrice(stat('sqm_price_apartment_'), swedish) },
        { key: 'housePrice', score: scores.housePrice, icon: VILLA_ICON, label: swedish ? 'Villa' : 'House', value: formatSqmPrice(stat('sqm_price_house_'), swedish) },
    ];
    return { scores, ownedShare, facts, prices };
};

// The note on an area the filters leave out (selected, but outside them), with the same funnel as on the map.
const FILTER_NOTE_ICON = '<svg class="area-card-note-icon" width="13" height="13" viewBox="-8 -8 16 16" aria-hidden="true"><path d="M-6.5 -5 H6.5 L1.8 1 V6 L-1.8 4 V1 Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>';

// filteredOut: the area is outside the filters (its card says so). shareable: a Dela button in the heading
// (the sticky cards and listvy's phone card, not the hover card, which can't be reached with the pointer).
export default function getTooltipData(name, regSo, colorCache, areaStats, scoreToColor, languageState, { filteredOut = false, shareable = false } = {}) {
    if (colorCache == null) {
        return '';
    }
    // The card's price-trend strip is filled in once the card is in the DOM (one observer, install-once).
    installAreaPriceTrendObserver();
    if (shareable) installAreaShareHandler();
    const swedish = Boolean(languageState?.swedish);
    const score = areaStats[`combined_${regSo}`];
    const hasScore = Number.isFinite(Number(score));
    const scoreColor = hasScore ? (scoreToColor[score] || '#8f9a97') : '#8f9a97';
    const areaLabel = hasScore ? getAreaLabel(score, swedish) : '';
    const { scores, ownedShare, facts, prices } = getAreaFacts(regSo, colorCache, areaStats, swedish);

    return `<div class="popup-content area-card">
        <div class="area-card-head">
            <span class="area-card-badge" style="--area-card-badge:${scoreColor};color:${scoreTextOn(scoreColor)}">
                ${HOUSE_ICON}
                ${hasScore ? `<span class="area-card-score">${swedish ? String(score).replace('.', ',') : score}</span>` : ''}
            </span>
            <div class="area-card-title">
                <span class="area-card-name">${name}</span>
                ${filteredOut
                    ? `<span class="area-card-note">${FILTER_NOTE_ICON}${swedish ? 'Utanför dina filter' : 'Outside your filters'}</span>`
                    : `<span class="area-card-label-row">${areaLabel ? `<span class="area-card-label">${areaLabel} ${swedish ? 'område' : 'area'}</span>` : ''}${shareable ? areaPriceTrendPlaceholder(regSo, swedish, true) : ''}</span>`}
            </div>
            ${filteredOut || shareable ? '' : areaPriceTrendPlaceholder(regSo, swedish)}
            ${shareable ? areaShareButton(regSo, name, swedish) : ''}
        </div>
        <p class="area-card-summary">${buildSummary({ scores, ownedShare }, swedish)}</p>
        <div class="area-card-facts">
            ${facts.map((fact) => `
            <div class="area-card-fact">
                <span class="area-card-fact-value">${fact.value}<i class="area-card-dot" style="background:${fact.color}"></i></span>
                <span class="area-card-fact-caption">${fact.caption}</span>
            </div>`).join('')}
        </div>
        <div class="area-card-prices">
            ${prices.map((price) => `
            <div class="area-card-price">
                <span class="area-card-price-label">${price.icon}${price.label}</span>
                <strong>${price.value}</strong>
            </div>`).join('')}
        </div>
    </div>`;
}
